import * as uuid from "uuid";
import {toBoolean, toString} from "../helpers/helpers";
import {K2GroupModel} from "./k2-group.model";

export class AreaGroupModel {
  static FLAGS = [
    "doUsageCollection",
    "doTonerMonitoring",
    "doTonerAutoOrder",
    "doInkMonitoring",
    "doInkAutoOrder",
    "doPrintHeadMonitoring",
    "doPrintHeadAutoOrder",
    "doMaintenanceMonitoring",
    "doMaintenanceAutoOrder",
    "doFastenerMonitoring",
    "doFastenerAutoOrder",
    "itsmConsumableAutoFulfillment",
    "itsmLongLifeConsumableAutoFulfillment",
  ];

  localId: string;
  id: string;
  areaName: string;
  groupName: string;
  asmCheckedDate: string;
  doUsageCollection: boolean;
  doTonerMonitoring: boolean;
  doTonerAutoOrder: boolean;
  doInkMonitoring: boolean;
  doInkAutoOrder: boolean;
  doPrintHeadMonitoring: boolean;
  doPrintHeadAutoOrder: boolean;
  doMaintenanceMonitoring: boolean;
  doMaintenanceAutoOrder: boolean;
  doFastenerMonitoring: boolean;
  doFastenerAutoOrder: boolean;
  itsmConsumableAutoFulfillment?: boolean;
  itsmLongLifeConsumableAutoFulfillment?: boolean;

  hasVerificationContactUploader: boolean;
  hasExceptionContactUploader: boolean;
  hasConsumableAddressUploader: boolean;
  hasServiceAddressUploader: boolean;
  disableEditReasons: string;

  duplicate: boolean;
  areaNameRequired: boolean;
  groupNameRequired: boolean;

  areaDisabled: boolean;
  groupDisabled: boolean;

  projectId: string;
  existsInK2: boolean;
  differsFromK2: boolean;
  allowEdit: boolean;

  get canonicalGroupName() {
    return this.groupName.toLowerCase();
  }

  get canonicalAreaName() {
    return this.areaName.toLowerCase();
  }

  get key() {
    return JSON.stringify([this.canonicalAreaName, this.canonicalGroupName]);
  }

  static converter = (json?: any) => new AreaGroupModel(json);

  constructor(json?: any) {
    json = json || {};

    this.localId = uuid.v1();
    this.id = toString(json.id);
    this.areaName = toString(json.areaName) || "";
    this.groupName = toString(json.groupName) || "";
    this.asmCheckedDate = parseDateFromJSON(json.asmCheckedDate) || null;

    this.doUsageCollection = toBoolean(json.doUsageCollection, true);
    this.doTonerMonitoring = toBoolean(json.doTonerMonitoring);
    this.doTonerAutoOrder = toBoolean(json.doTonerAutoOrder);
    this.doInkMonitoring = toBoolean(json.doInkMonitoring);
    this.doInkAutoOrder = toBoolean(json.doInkAutoOrder);
    this.doPrintHeadMonitoring = toBoolean(json.doPrintHeadMonitoring);
    this.doPrintHeadAutoOrder = toBoolean(json.doPrintHeadAutoOrder);
    this.doMaintenanceMonitoring = toBoolean(json.doMaintenanceMonitoring);
    this.doMaintenanceAutoOrder = toBoolean(json.doMaintenanceAutoOrder);
    this.doFastenerMonitoring = toBoolean(json.doFastenerMonitoring);
    this.doFastenerAutoOrder = toBoolean(json.doFastenerAutoOrder);
    this.itsmConsumableAutoFulfillment = toBoolean(json.itsmConsumableAutoFulfillment);
    this.itsmLongLifeConsumableAutoFulfillment = toBoolean(json.itsmLongLifeConsumableAutoFulfillment);

    this.hasVerificationContactUploader = toBoolean(json.hasVerificationContactUploader, true);
    this.hasExceptionContactUploader = toBoolean(json.hasExceptionContactUploader, true);
    this.hasConsumableAddressUploader = toBoolean(json.hasConsumableAddressUploader, true);
    this.hasServiceAddressUploader = toBoolean(json.hasServiceAddressUploader, true);
    this.disableEditReasons = toString(json.disableEditReasons) || "";

    this.areaDisabled = toBoolean(json.areaDisabled);
    this.groupDisabled = toBoolean(json.groupDisabled);

    this.duplicate = toBoolean(json.duplicate);
    this.areaNameRequired = toBoolean(json.areaNameRequired);
    this.groupNameRequired = toBoolean(json.groupNameRequired);

    this.projectId = toString(json.projectId);
    this.existsInK2 = toBoolean(json.existsInK2);
    this.differsFromK2 = toBoolean(json.differsFromK2);

    this.allowEdit = toBoolean(json.allowEdit, !this.existsInK2);
  }

  importDataFromK2Group(k2Group: K2GroupModel) {
    this.areaName = k2Group.areaName;
    this.groupName = k2Group.groupName;

    this.existsInK2 = true;
    this.allowEdit = false;
    k2Group.addedToProject = true;

    for (const flag of AreaGroupModel.FLAGS) {
      this[flag] = k2Group[flag];
    }

    this.hasVerificationContactUploader = k2Group.hasVerificationContactUploader;
    this.hasExceptionContactUploader = k2Group.hasExceptionContactUploader;
    this.hasConsumableAddressUploader = k2Group.hasConsumableAddressUploader;
    this.hasServiceAddressUploader = k2Group.hasServiceAddressUploader;
    this.disableEditReasons = k2Group.disableEditReasons;
  }

  clean() {
    this.areaName = this.areaName ? this.areaName.trim() : "";
    this.groupName = this.groupName ? this.groupName.trim() : "";
  }

  change() {
    if (this.existsInK2) this.differsFromK2 = true;
  }

  emeaDataChanged() {
    this.doTonerAutoOrder = this.doTonerMonitoring;
    this.doInkMonitoring = this.doTonerMonitoring;
    this.doInkAutoOrder = this.doTonerMonitoring;
    this.doPrintHeadAutoOrder = this.doPrintHeadMonitoring;
    this.doFastenerAutoOrder = this.doFastenerMonitoring;
    this.change();
  }

  usageCollectionChanged(): void {
    if (!this.doUsageCollection) {
      this.doTonerMonitoring = false;
      this.doTonerAutoOrder = false;
      this.doInkMonitoring = false;
      this.doInkAutoOrder = false;
      this.doPrintHeadMonitoring = false;
      this.doPrintHeadAutoOrder = false;
      this.doMaintenanceMonitoring = false;
      this.doMaintenanceAutoOrder = false;
      this.doFastenerMonitoring = false;
      this.doFastenerAutoOrder = false;
      this.itsmConsumableAutoFulfillment = false;
      this.itsmLongLifeConsumableAutoFulfillment = false;
    }
    this.change();
  }
}

function parseDateFromJSON(date) {
  if (!date) return null;
  const matches = `${date}`.match(/^([0-9]{4}-[0-9]{2}-[0-9]{2})T?/i);
  if (!matches) return null;
  return matches[1] || null;
}

export function areaGroupModelFactory(json?: any): AreaGroupModel {
  return new AreaGroupModel(json);
}
